import { useMapDistanceMeasuring } from "@/composition/useMapDistanceMeasuring"
import { LatLng, type Nullable } from "@/models"
import { calcDistance } from "@/utils/distance"
import { LatLngFormatType, fixLatLng, formatLatLng } from "@/utils/formatting/formatLatLng"
import { watchDeep, watchImmediate } from "@vueuse/core"
import L, { Map, Polyline, Popup } from "leaflet"
import { css } from "vite-css-in-js"
import { computed, shallowRef, toRef, watch } from "vue"
import { Modal, ModalTitle, defineComponent, inferProps, refSetter, type ReactiveComponent } from "vue-utils"
import BootstrapButton from "../form/BootstrapButton"
import LeafletMap from "./LeafletMap"
import LocationMarker from "./LocationMarker"
import SelectTypeToggle, { LocationSelectType } from "./SelectTypeToggle"
import CoordinateInput from "../CoordinateInput"

interface Props {
	locationName: string
	location: LatLng
	showLatLngInputs: boolean
	setLocation(location: LatLng): void
	cancel(): void
	markerFilter?: string
	readOnly?: boolean
	submitting?: boolean
}

const mapStyles = css`
	cursor: crosshair;

	.leaflet-interactive,
	.leaflet-map-pane,
	.leaflet-control-container,
	.leaflet-grab {
		cursor: crosshair;
	}
	.leaflet-drag-target {
		cursor: grab;
	}
`

const MapLocationSelect: ReactiveComponent<Props> = (props) => {
	const currentlySelected = shallowRef<LatLng>(props.location)
	const distanceStartCoords = shallowRef<Nullable<LatLng>>(null)
	const distanceEndCoords = shallowRef<Nullable<LatLng>>(null)
	const selectType = shallowRef<LocationSelectType>(LocationSelectType.Point)
	const { MeasureDistance, initMeasuring, measureData } = useMapDistanceMeasuring()
	const currentlyPopulated = computed(() => currentlySelected.value && Number.isFinite(currentlySelected.value.longitude) && Number.isFinite(currentlySelected.value.longitude))

	let map: Map
	let pin: LocationMarker | null = null
	let distanceStart: LocationMarker | null = null
	let distanceLine: Polyline | null = null
	let distancePopup: Popup | null = null


	function initMap(lMap: Map) {
		map = lMap

		if (currentlyPopulated.value) {
			map.setView([props.location.latitude, props.location.longitude], map.getZoom(), {
				animate: false,
			})
		}

		initMeasuring(lMap)

		updatePin()
		lMap.addEventListener("click", (clickEvent) => {
			if (props.readOnly || measureData.value.isMeasuring) {
				return
			}

			if (selectType.value === LocationSelectType.Point) {
				if (distanceLine) {
					distanceLine.remove()
					distancePopup = null
					distanceStartCoords.value = null
				}

				currentlySelected.value = {
					latitude: clickEvent.latlng.lat,
					longitude: clickEvent.latlng.lng,
				}
			} else if (selectType.value === LocationSelectType.Distance) {
				if (distanceLine) {
					distanceLine.remove()
				}

				if (!distanceStartCoords.value) {
					currentlySelected.value = {latitude: NaN, longitude: NaN}
					distanceStartCoords.value = {
						latitude: clickEvent.latlng.lat,
						longitude: clickEvent.latlng.lng,
					}
				} else {
					distancePopup = null
					distanceStartCoords.value = null
					currentlySelected.value = {
						latitude: clickEvent.latlng.lat,
						longitude: clickEvent.latlng.lng,
					}
				}
			}
		})
		lMap.addEventListener("mousemove", (mouseEvent) => {
			if (!distanceStartCoords.value ||
				distanceEndCoords.value ||
				!!props.readOnly ||
				selectType.value === LocationSelectType.Point ||
				currentlyPopulated.value ||
				measureData.value.isMeasuring
			) {
				return
			}

			if (distanceLine) {
				distanceLine.remove()
			}

			const latLngTo: LatLng = { latitude: mouseEvent.latlng.lat, longitude: mouseEvent.latlng.lng }
			const calculatedDistance = calcDistance(distanceStartCoords.value, latLngTo)

			distanceStart?.setRadius(calculatedDistance.distance)
			distanceStart?.setDirection(calculatedDistance.bearing, calculatedDistance.angle)

			if (!distancePopup) {
				distancePopup = L.popup({
					closeButton: false,
					closeOnEscapeKey: false,
					offset: [150, 40],
					className: "direction-popup",
				})
					.setLatLng(calculatedDistance.to)
					.setContent(calculatedDistance.distanceText)
					.openOn(map)
			}
			distancePopup.setLatLng(calculatedDistance.to)
			distancePopup.setContent(calculatedDistance.distanceText)

			distanceLine = L.polyline(calculatedDistance.coords, { color: "#b4611c" })
			distanceLine.addTo(map)
		})
	}

	function updatePin() {
		if (!map) {
			return
		}

		const loc = currentlySelected.value
		if (!currentlyPopulated.value) {
			if (pin) {
				pin.removeFrom(map)
				pin = null
			}
			return
		}

		const tooltipDiv = document.createElement("div")

		const title = document.createElement("span")
		title.innerText = props.locationName

		const fixedLoc = fixLatLng(loc)

		tooltipDiv.append(title)
		tooltipDiv.append(document.createElement("br"))
		tooltipDiv.append(new Text(formatLatLng(fixedLoc, LatLngFormatType.DegreesDecimalMinutes)))

		if (pin) {
			pin.moveTo(loc.latitude, loc.longitude)
			pin.locationMarker.getTooltip()?.setContent(tooltipDiv?.innerHTML ?? "")
		} else {
			pin = new LocationMarker({
				latitude: loc.latitude,
				longitude: loc.longitude,
				tooltipHtml: tooltipDiv?.innerHTML ?? "",
				tooltipPermanent: true,
			})

			if (props.markerFilter !== undefined) {
				pin.setFilter(props.markerFilter)
			}

			pin.addTo(map)
		}
	}

	function startDistance() {
		if (!map) {
			return
		}

		const loc = distanceStartCoords.value
		if (!loc) {
			if (distanceStart) {
				distanceStart.removeFrom(map)
				distanceStart = null
			}
			return
		}

		if (distanceStart) {
			distanceStart.moveTo(loc.latitude, loc.longitude)
		} else {
			distanceStart = new LocationMarker({
				latitude: loc.latitude,
				longitude: loc.longitude,
				radius: 0,
				radiusOptions: {
					color: "#ff8612",
				},
				markerStyles: {
					filter: "hue-rotate(180deg) brightness(95%)",
				},
			})
			distanceStart.addTo(map)
		}
	}

	function handleConfirm() {
		if (currentlyPopulated.value) {
			props.setLocation(fixLatLng(currentlySelected.value))
		}
	}

	watch(toRef(props, "location"), (newLoc) => (currentlySelected.value = newLoc))
	watchImmediate(toRef(props, "locationName"), updatePin)
	watchImmediate(distanceStartCoords, startDistance)
	watchDeep(currentlySelected, updatePin)

	return () => (
		<Modal zIndex={4} style={{ width: "80%", height: "90%" }} class="flex flex-col" onCancel={props.cancel}>
			<ModalTitle title={`${props.readOnly ? "View" : "Select"} ${props.locationName}`} />
			<hr />
			{(props.showLatLngInputs && !props.readOnly) && (
				<>
				<div class="d-flex gap-5">
					<label class="d-flex flex-row align-items-center">
						Latitude
						<CoordinateInput
							type="latitude"
							value={currentlySelected.value.latitude}
							setValue={(lat) => {
								currentlySelected.value.latitude = lat
								updatePin()
							}}
							
						/>
					</label>
					<label class="d-flex flex-row align-items-center">
						Longitude
						<CoordinateInput
							type="longitude"
							value={currentlySelected.value.longitude}
							setValue={(long) => {
								currentlySelected.value.longitude = long
								updatePin()
							}}
						/>
					</label>
				</div>
				<hr />
				</>
			)}
			<LeafletMap
				initMap={initMap}
				zoomControlPosition="bottomright"
				class={["flex-grow-1", props.readOnly ? undefined : mapStyles]}
			>
				<MeasureDistance />
				{!props.readOnly && <SelectTypeToggle selectType={selectType.value} setSelectType={refSetter(selectType)} />}
			</LeafletMap>
			<hr />
			<div class="flex items-center justify-end spacing-4">
				<BootstrapButton color="secondary" onClick={props.cancel}>
					{props.readOnly ? "Close" : "Cancel"}
				</BootstrapButton>
				{!props.readOnly && (
					<BootstrapButton
						color="primary"
						onClick={handleConfirm}
						disabled={!currentlySelected.value}
						isSubmitting={props.submitting}
					>
						Select Location
					</BootstrapButton>
				)}
			</div>
		</Modal>
	)
}

export default defineComponent(MapLocationSelect, inferProps<Props>())
